import React from 'react';
import { useScenarios } from '../../hooks/useScenarios';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScenarioCard } from './ScenarioCard';
import { BaseForecastCard } from './BaseForecastCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Search,
  Star,
  Grid3x3,
  LayoutList,
  Plus,
  Sparkles,
  TrendingUp,
  Scissors,
  AlertTriangle,
  Layers,
  Eye,
  Edit,
  GitCompare,
} from 'lucide-react';
import { ComprehensiveScenario } from '../../types/scenarios';

interface ScenarioListGalleryProps {
  onSelectScenario: (id: string) => void;
  onEditScenario?: (id: string) => void;
  onCompareScenario?: (id: string) => void;
  onCreateScenario?: (template?: 'growth' | 'cost-cutting' | 'crisis') => void;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  categoryFilter: string;
  setCategoryFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  sortBy: 'recent' | 'name' | 'impact';
  setSortBy: (v: 'recent' | 'name' | 'impact') => void;
  viewMode: 'grid' | 'list';
  setViewMode: (v: 'grid' | 'list') => void;
}

export const ScenarioListGallery: React.FC<ScenarioListGalleryProps> = ({ 
  onSelectScenario,
  onEditScenario,
  onCompareScenario,
  onCreateScenario,
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  sortBy,
  setSortBy,
  viewMode,
  setViewMode,
}) => {
  const { createScenario } = useScenarios();
  const scenarios = useVisibleScenarios();

  // Handle Clone
  const handleClone = async (id: string) => {
    const scenario = scenarios.find(s => s.id === id);
    if (!scenario) return;

    const clonedScenario = {
      ...scenario,
      name: `${scenario.name} (Copy)`,
      status: 'draft' as const,
      createdByName: scenario.createdByName, // Will be updated by context
      isFavorite: false,
      commentCount: 0,
      comments: [],
      shares: [],
      collectionIds: [],
      tags: [...scenario.tags],
      changes: [...scenario.changes],
    };

    await createScenario(clonedScenario);
  };

  // Handle Clone Base — creates a new Draft scenario seeded from base data
  const handleCloneBase = async () => {
    const baseClone = {
      name: 'Base Forecast (Clone)',
      description: 'Cloned from the Base Forecast. Adjust this scenario to model a what-if view.',
      status: 'draft' as const,
      category: 'custom' as const,
      priority: 'medium' as const,
      isFavorite: false,
      commentCount: 0,
      comments: [],
      shares: [],
      collectionIds: [],
      tags: ['base-clone'],
      changes: [],
      metrics: {
        netImpact: 0,
        minCash: 0,
        runway: 0,
        probabilityOfSuccess: 100,
      },
      approvals: [],
      versions: [],
    };
    await createScenario(baseClone as unknown as Parameters<typeof createScenario>[0]);
  };

  // Handle Edit
  const handleEdit = (id: string) => {
    if (onEditScenario) {
      onEditScenario(id);
    }
  };

  // Handle Compare
  const handleCompare = (id: string) => {
    if (onCompareScenario) {
      onCompareScenario(id);
    }
  };

  // Categorise scenarios
  const categoriseScenario = (scenario: ComprehensiveScenario) => {
    // If scenario has an explicit category set, use it
    if (scenario.category) {
      return scenario.category;
    }

    // Otherwise, calculate based on changes and metrics
    const hasRevenue = scenario.changes.some(c => 
      c.type === 'win-customer' || c.type === 'add-revenue'
    );
    const hasCostReductions = scenario.changes.some(c => 
      c.type === 'change-costs' && c.amount > 0 // Positive amount = cost reduction/savings
    );
    const hasCostIncreases = scenario.changes.some(c => 
      c.type === 'change-costs' && c.amount < 0 // Negative amount = cost increase
    );
    const hasNegativeImpact = scenario.metrics.netImpact < 0;
    const isUrgent = scenario.priority === 'critical' || scenario.priority === 'high';
    const netImpact = scenario.metrics.netImpact;

    // Crisis: negative impact + urgent priority
    if (hasNegativeImpact && isUrgent) return 'crisis';
    
    // Growth: has revenue OR positive net impact with cost increases (investment scenarios)
    if (hasRevenue || (netImpact > 0 && hasCostIncreases)) return 'growth';
    
    // Cost-Cutting: has cost reductions OR focuses on reducing costs
    if (hasCostReductions || (netImpact > 0 && !hasRevenue && !hasCostIncreases)) return 'cost-cutting';
    
    // Uncategorised: system couldn't derive a category — falls into "All" only
    return null;
  };

  // Filter scenarios
  let filteredScenarios = scenarios.filter(scenario => {
    // Search filter
    if (searchTerm && !scenario.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !scenario.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Category filter — null (uncategorised) only shows under 'all'
    if (categoryFilter !== 'all') {
      const category = categoriseScenario(scenario);
      if (!category || category !== categoryFilter) return false;
    }

    // Status filter — all 8 statuses
    if (statusFilter !== 'all') {
      if (statusFilter === 'ready') {
        return scenario.status === 'active';
      }
      return scenario.status === statusFilter;
    }

    return true;
  });

  // Sort scenarios
  filteredScenarios = [...filteredScenarios].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'impact') {
      return Math.abs(b.metrics.netImpact) - Math.abs(a.metrics.netImpact);
    } else {
      return b.modifiedAt.getTime() - a.modifiedAt.getTime();
    }
  });

  // Count by category
  const categoryCounts = {
    all: scenarios.length,
    growth: scenarios.filter(s => categoriseScenario(s) === 'growth').length,
    'cost-cutting': scenarios.filter(s => categoriseScenario(s) === 'cost-cutting').length,
    crisis: scenarios.filter(s => categoriseScenario(s) === 'crisis').length,
  };

  // Count by status
  const statusCounts = {
    all: scenarios.length,
    draft: scenarios.filter(s => s.status === 'draft').length,
    'under-review': scenarios.filter(s => s.status === 'under-review').length,
    'pending-approval': scenarios.filter(s => s.status === 'pending-approval').length,

    active: scenarios.filter(s => s.status === 'active').length,
    parked: scenarios.filter(s => s.status === 'parked').length,
    rejected: scenarios.filter(s => s.status === 'rejected').length,
  };

  // Empty state
  if (scenarios.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6">
          <Sparkles className="h-10 w-10 text-blue-600" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">
          Create your first scenario
        </h3>
        <p className="text-gray-600 text-center max-w-md mb-8">
          Model different business outcomes and explore their impact on your cash flow. 
          Start by creating a scenario from a template or building your own.
        </p>
        <Button size="lg" className="gap-2" onClick={() => onCreateScenario?.()}>
          <Plus className="h-5 w-5" />
          Create Scenario
        </Button>

        {/* Example Templates */}
        <div className="mt-12 w-full max-w-4xl">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Popular Templates</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => onCreateScenario?.('growth')}
              className="text-left"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-green-500 h-full">
                <CardContent className="p-4">
                  <TrendingUp className="h-8 w-8 text-green-600 mb-3" />
                  <h5 className="font-semibold mb-1">Growth Scenario</h5>
                  <p className="text-sm text-gray-600">Model new customer wins and revenue growth</p>
                </CardContent>
              </Card>
            </button>
            <button 
              onClick={() => onCreateScenario?.('cost-cutting')}
              className="text-left"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-blue-500 h-full">
                <CardContent className="p-4">
                  <Scissors className="h-8 w-8 text-blue-600 mb-3" />
                  <h5 className="font-semibold mb-1">Cost Reduction</h5>
                  <p className="text-sm text-gray-600">Explore cost-cutting opportunities</p>
                </CardContent>
              </Card>
            </button>
            <button 
              onClick={() => onCreateScenario?.('crisis')}
              className="text-left"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-amber-500 h-full">
                <CardContent className="p-4">
                  <AlertTriangle className="h-8 w-8 text-amber-600 mb-3" />
                  <h5 className="font-semibold mb-1">Crisis Planning</h5>
                  <p className="text-sm text-gray-600">Prepare for challenging situations</p>
                </CardContent>
              </Card>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Base Forecast — always pinned at top */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900 mb-3">
          Ground Truth
        </h2>
        <BaseForecastCard
          onView={() => {/* Base view — TODO wire to base detail view */}}
          onCompare={() => { if (onCompareScenario) onCompareScenario('base'); }}
          onClone={handleCloneBase}
        />
      </div>

      {/* Your Scenarios — divider + filters inline */}
      <div className="flex items-center gap-4">
        <h2 className="text-lg font-semibold text-neutral-900 whitespace-nowrap">
          Your Scenarios
        </h2>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Filters Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search scenarios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-2">
              {[
                { value: 'all', label: 'All', icon: <Layers className="h-3.5 w-3.5" />, count: categoryCounts.all },
                { value: 'growth', label: 'Growth', icon: <TrendingUp className="h-3.5 w-3.5" />, count: categoryCounts.growth },
                { value: 'cost-cutting', label: 'Cost-Cutting', icon: <Scissors className="h-3.5 w-3.5" />, count: categoryCounts['cost-cutting'] },
                { value: 'crisis', label: 'Crisis', icon: <AlertTriangle className="h-3.5 w-3.5" />, count: categoryCounts.crisis },
              ].map(({ value, label, icon, count }) => (
                <Button
                  key={value}
                  variant={categoryFilter === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter(value)}
                  className="gap-1.5"
                >
                  {icon}
                  {label}
                  <Badge
                    variant={categoryFilter === value ? 'secondary' : 'secondary'}
                    className="ml-0.5"
                  >
                    {count}
                  </Badge>
                </Button>
              ))}
            </div>

            {/* Status Filter — all 8 statuses */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status ({statusCounts.all})</SelectItem>
                <SelectItem value="draft">Draft {statusCounts.draft > 0 ? `(${statusCounts.draft})` : ''}</SelectItem>
                <SelectItem value="under-review">Under Review {statusCounts['under-review'] > 0 ? `(${statusCounts['under-review']})` : ''}</SelectItem>
                <SelectItem value="pending-approval">Pending Approval {statusCounts['pending-approval'] > 0 ? `(${statusCounts['pending-approval']})` : ''}</SelectItem>
                <SelectItem value="active">Active {statusCounts.active > 0 ? `(${statusCounts.active})` : ''}</SelectItem>
                <SelectItem value="parked">Parked {statusCounts.parked > 0 ? `(${statusCounts.parked})` : ''}</SelectItem>
                <SelectItem value="rejected">Rejected {statusCounts.rejected > 0 ? `(${statusCounts.rejected})` : ''}</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Recent</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="impact">Impact</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="px-3"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="px-3"
              >
                <LayoutList className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing <span className="font-semibold">{filteredScenarios.length}</span> of <span className="font-semibold">{scenarios.length}</span> scenario{scenarios.length !== 1 ? 's' : ''}
        </p>
        {(searchTerm || categoryFilter !== 'all' || statusFilter !== 'all') && (
          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('all');
              setStatusFilter('all');
            }}
            className="text-sm text-primary hover:underline"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Scenario Cards */}
      {filteredScenarios.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredScenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                onSelect={onSelectScenario}
                onEdit={handleEdit}
                onCompare={handleCompare}
                onClone={handleClone}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-border rounded-lg border bg-white overflow-hidden">
            {filteredScenarios.map((scenario) => (
              <div
                key={scenario.id}
                className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                style={{ borderLeft: `3px solid var(--mc-${scenario.category === 'growth' ? 'success' : scenario.category === 'crisis' ? 'error' : scenario.category === 'cost-cutting' ? 'info' : 'warning'}-border)` }}
              >
                {/* Name + description */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">{scenario.name}</p>
                  <p className="text-xs text-neutral-500 truncate">{scenario.description}</p>
                </div>

                {/* Category */}
                <Badge variant="outline" className="text-xs shrink-0 hidden md:flex">
                  {scenario.category}
                </Badge>

                {/* Status */}
                <Badge
                  variant={
                    scenario.status === 'active' || scenario.status === 'committed' ? 'success'
                    : scenario.status === 'pending-approval' ? 'warning'
                    : scenario.status === 'rejected' ? 'outline'
                    : 'secondary'
                  }
                  className="text-xs shrink-0 capitalize hidden sm:flex"
                >
                  {scenario.status.replace(/-/g, ' ')}
                </Badge>

                {/* Net impact */}
                <span className={`text-xs font-semibold shrink-0 hidden lg:block ${scenario.metrics.netImpact >= 0 ? 'text-green-600' : 'text-neutral-700'}`}>
                  {scenario.metrics.netImpact >= 0 ? '+' : ''}{(scenario.metrics.netImpact / 1000).toFixed(0)}K
                </span>

                {/* Owner + date */}
                <span className="text-xs text-neutral-400 shrink-0 hidden xl:block">{scenario.createdBy} · {new Date(scenario.modifiedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>

                {/* Action buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onSelectScenario(scenario.id); }}>
                    <Eye className="h-3.5 w-3.5" /> View
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleEdit(scenario.id); }}>
                    <Edit className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleCompare(scenario.id); }}>
                    <GitCompare className="h-3.5 w-3.5" /> Compare
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No scenarios found
            </h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your filters or search terms
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setStatusFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
